import React from 'react';
import groupBy from 'lodash/groupBy';
import { FormattedMessage } from 'react-intl';
import { Table, TableBody, TableCell, TableHeader, TableRow } from 'semantic-ui-react';
import If from 'Views/Components/If/If';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { isNotNullOrUndefined, isNullOrUndefined } from 'Util/TypeGuards';
import { UnitLabels } from 'Util/UnitOfMeasurementUtils';
import { PricingStrategyIds } from 'Global/Pricing/PricingStrategy';
import { PriceCellState } from 'Global/Pricing/PriceCellState';
import { PricingRouteTabs, SegmentHeaderCells } from './PricingRouteTabs';
import { UserPriceCell } from './UserPriceCell';
import { PricingStrategyKind, UserPriceRow } from './userPricingRows';

interface UserFreightPricingTableProps {
	rows: UserPriceRow[];
	onCellWrite: (target: UserPriceRow, next: PriceCellState) => void;
	isReadOnly?: boolean;
	errors?: { [key: string]: string | undefined };
	pricingStrategy: PricingStrategyKind;
	/** Which freight dimension this table prices — mirrors the two legacy freight tables. */
	dimension: 'category' | 'handling';
}

/**
 * Form-state fork of FreightCategoryPricingTable / FreightHandlingPricingTable — they differed
 * only in filter, label source and rate header, so the fork is one component with a dimension
 * switch. Missing per-segment slots render as virtual rows and materialise on first edit.
 */
export function UserFreightPricingTable({
	rows,
	onCellWrite,
	isReadOnly = false,
	errors,
	pricingStrategy,
	dimension,
}: UserFreightPricingTableProps) {
	const isCategory = dimension === 'category';
	const labelOf = (row: UserPriceRow) =>
		isCategory ? row.freightPriceRange?.freightCategoryLabel : row.freightPriceRange?.freightHandlingLabel;

	// Preserved legacy guard (note: freightPriceRangeId !== null is true for undefined too, so
	// in practice this only hides the table when there are no rows at all).
	if (rows.length === 0 || !rows.some(row => row.freightPriceRangeId !== null)) {
		return null;
	}

	return (
		<PricingRouteTabs pricingStrategy={pricingStrategy}>
			{(route, segmentsSorted) => (
				<Table unstackable={isCategory}>
					<TableHeader>
						<TableRow>
							<TableCell textAlign="left">
								{isCategory ? 'Freight category' : 'Freight handling'}
							</TableCell>
							<TableCell textAlign="left">
								<FormattedMessage
									id="freight-pricing__max-value-table__header"
									defaultMessage="Max value (t/{cubic_unit})"
									values={{ cubic_unit: whiteLabelStore.volumeUnitLabel }}
								/>
							</TableCell>
							<If condition={pricingStrategy === 'Fixed'}>
								<TableCell textAlign="left">
									{isCategory ? 'Rate' : `Rate (per ${UnitLabels.mass.tonne})`}
								</TableCell>
							</If>
							<If condition={pricingStrategy === 'ConsecutiveStops'}>
								<SegmentHeaderCells segments={segmentsSorted} />
							</If>
						</TableRow>
					</TableHeader>
					<TableBody>
						{Object.entries(
							groupBy(
								rows
									.slice()
									.filter(
										row =>
											isNotNullOrUndefined(row.freightPriceRangeId) &&
											(isCategory
												? isNotNullOrUndefined(row.freightPriceRange?.freightCategoryId)
												: isNotNullOrUndefined(row.freightPriceRange?.freightHandlingId)) &&
											((isNullOrUndefined(route.id) && isNullOrUndefined(row.routeId)) ||
												row.routeId === route.id),
									)
									.sort((a, b) => {
										const labelComparison = (labelOf(a) || '').localeCompare(labelOf(b) || '');
										if (labelComparison !== 0) {
											return labelComparison;
										}
										return (
											(a.freightPriceRange?.maxValue || 0) - (b.freightPriceRange?.maxValue || 0)
										);
									}),
								row => row.freightPriceRangeId,
							),
						).map(([key, groupedRows]) => (
							<TableRow key={key}>
								<TableCell textAlign="left">
									{labelOf(groupedRows[0])}
									{groupedRows[0].freightPriceRange?.isFixedPrice ? ' (Fixed)' : ''}
								</TableCell>
								<TableCell textAlign="left">{groupedRows[0].freightPriceRange?.maxValue}</TableCell>
								<If condition={pricingStrategy === 'Fixed'}>
									<TableCell textAlign="left">
										<UserPriceCell
											row={groupedRows[0]}
											onWrite={next => onCellWrite(groupedRows[0], next)}
											isReadOnly={isReadOnly}
											error={isNullOrUndefined(groupedRows[0].amount) && !!errors?.prices}
										/>
									</TableCell>
								</If>
								<If condition={pricingStrategy === 'ConsecutiveStops'}>
									{segmentsSorted.map(segment => {
										const found = groupedRows.find(p => p.startStopId === segment.startStop.id);
										const cellRow: UserPriceRow = found ?? {
											freightPriceRangeId: key,
											freightPriceRange: groupedRows[0]?.freightPriceRange,
											routeId: route.id,
											startStopId: segment.startStop.id,
											amount: null,
											mode: 'FIXED',
											pricingStrategyId: PricingStrategyIds[pricingStrategy],
										};
										return (
											<TableCell key={segment.id} textAlign="left">
												<UserPriceCell
													row={cellRow}
													onWrite={next => onCellWrite(cellRow, next)}
													isReadOnly={isReadOnly}
													error={isNullOrUndefined(cellRow.amount) && !!errors?.prices}
												/>
											</TableCell>
										);
									})}
								</If>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}
		</PricingRouteTabs>
	);
}
